import { JSX, ReactNode, forwardRef } from 'react';
import cn from 'classnames';

import styles from './gridItem.module.scss';
import { generateResponsiveClasses, TResponsiveValue } from '../shared/responsive-classes';
import { processInlineStyles } from '../shared/style-processors';

export type { TResponsiveValue };

export interface IGridItemProps {
  children?: ReactNode,
  className?: string,
  colSpan?: TResponsiveValue<number> | TResponsiveValue<'full'>,
  rowSpan?: TResponsiveValue<number> | TResponsiveValue<'full'>,
  colStart?: TResponsiveValue<number> | TResponsiveValue<'auto'>,
  rowStart?: TResponsiveValue<number> | TResponsiveValue<'auto'>,
  dataTest?: string,
  as?: keyof JSX.IntrinsicElements,
  style?: React.CSSProperties,
}

const GridItem = forwardRef<HTMLDivElement, IGridItemProps>(({
  children,
  className,
  colSpan,
  rowSpan,
  colStart,
  rowStart,
  dataTest = 'gridItem',
  as: Component = 'div' as any,
  style,
  ...props
}, ref) => {
  const gridItemClasses = cn(
    styles.gridItem,
    generateResponsiveClasses(colSpan, 'colSpan', styles),
    generateResponsiveClasses(rowSpan, 'rowSpan', styles),
    generateResponsiveClasses(colStart, 'colStart', styles),
    generateResponsiveClasses(rowStart, 'rowStart', styles),
    className,
  );

  const inlineStyles = processInlineStyles({ colSpan, rowSpan, colStart, rowStart }, styles);

  const combinedStyles = { ...inlineStyles, ...style };

  return (
    <Component
      ref={ ref }
      className={ gridItemClasses }
      data-test={ dataTest }
      style={ combinedStyles }
      { ...props }
    >
      { children }
    </Component>
  );
});

export default GridItem;
